/**
 * PlanningChat — Lightweight chat wrapper for planning/review sessions.
 * Reuses sendMessageStream(). No StateCheckIn, no session sidebar.
 * Shows a planning-mode banner and streams conversation.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader2, Sun, Moon, ArrowRight, Volume2, VolumeX, Zap, Check } from 'lucide-react';
import { sendMessageStream, sendMessage, endCheckIn, setVoiceDefaultOn } from '../lib/api';
import { useVoiceConversation } from '../lib/useVoiceConversation';
import VoiceInput from './VoiceInput';

export default function PlanningChat({ sessionId, mode, openerText, onComplete, voiceEnabled = false, voiceId, voiceDefaultOn = true }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [showContinue, setShowContinue] = useState(false);
  const [closing, setClosing] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const isCheckIn = mode === 'check_in';

  const voice = useVoiceConversation({
    enabled: voiceEnabled,
    defaultOn: voiceDefaultOn,
    onTranscribed: (text) => handleSendWithText(text),
    voiceId,
    onVoiceModeChange: (next) => { setVoiceDefaultOn(next).catch(() => {}); },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Open the conversation. If the server generated an opener, render it as
  // the first synthetic assistant bubble (Navigator speaks first). Otherwise
  // fall back to the legacy user-prime auto-send so the AI still goes first.
  useEffect(() => {
    if (messages.length > 0 || !sessionId) return;

    if (openerText) {
      setMessages([{
        role: 'assistant',
        content: openerText,
        timestamp: new Date().toISOString(),
        synthetic: true,
      }]);
      if (voice.voiceMode && voice.speak) {
        voice.speak(openerText);
      }
      return;
    }

    const openingMessage = mode === 'review'
      ? "Let's review my day."
      : mode === 'check_in'
        ? "I'm here for a quick check-in."
        : "Let's plan my day.";
    handleSendWithText(openingMessage);
  }, [sessionId, openerText]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendWithText = async (text) => {
    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      await handleSendStreaming(updatedMessages);
    } catch {
      try {
        await handleSendNonStreaming(updatedMessages);
      } catch {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Connection error. Please try again.',
          timestamp: new Date().toISOString()
        }]);
      }
    } finally {
      setLoading(false);
      setStreaming(false);
    }
  };

  const handleSendStreaming = async (updatedMessages) => {
    let assistantText = '';
    const assistantMsg = { role: 'assistant', content: '', timestamp: new Date().toISOString(), thinking: true };
    setMessages([...updatedMessages, assistantMsg]);
    setStreaming(true);
    voice.beginUtterance();

    let planCreated = false;
    let reviewSaved = false;

    // Synthetic messages (the opener) are display-only — strip before send
    // or they get duplicated into Anthropic context.
    const apiMessages = updatedMessages.filter(m => !m.synthetic);

    await sendMessageStream(apiMessages, sessionId, (event) => {
      switch (event.type) {
        case 'text_delta':
          assistantText += event.text;
          voice.feedTextDelta(event.text);
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], content: assistantText, thinking: false };
            return updated;
          });
          break;
        case 'tool_start':
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            // Show user-friendly label for plan/review tools
            const friendlyName = event.name === 'create_daily_actions' ? 'Building your plan'
              : event.name === 'update_daily_plan_review' ? 'Saving your review'
              : event.name;
            if (last?.role === 'assistant' && !last.content) {
              updated[updated.length - 1] = { ...last, thinking: true, toolName: friendlyName };
            }
            return updated;
          });
          break;
        case 'tool_result':
          if (event.name === 'create_daily_actions' && !event.is_error) {
            planCreated = true;
          }
          if (event.name === 'update_daily_plan_review' && !event.is_error) {
            reviewSaved = true;
          }
          break;
        case 'done':
          // Show continue button so user can read the final message
          if (planCreated || reviewSaved) {
            setShowContinue(true);
          }
          break;
        case 'error':
          assistantText += (assistantText ? '\n\n' : '') + `Error: ${event.message}`;
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], content: assistantText };
            return updated;
          });
          break;
        default:
          break;
      }
    });

    voice.endUtterance();
  };

  const handleSendNonStreaming = async (updatedMessages) => {
    const apiMessages = updatedMessages.filter(m => !m.synthetic);
    const result = await sendMessage(apiMessages, sessionId);
    if (result.response) {
      setMessages([...updatedMessages, {
        role: 'assistant',
        content: result.response,
        timestamp: new Date().toISOString()
      }]);
    }
  };


  const handleSend = () => {
    const text = input.trim();
    if (!text || loading) return;
    handleSendWithText(text);
  };

  const autoGrow = useCallback((el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const ModeIcon = isCheckIn ? Zap : mode === 'review' ? Moon : Sun;
  const modeLabel = isCheckIn ? 'Quick Check-in' : mode === 'review' ? 'Evening Review' : 'Morning Planning';
  const modeColor = isCheckIn ? 'text-emerald-400' : mode === 'review' ? 'text-indigo-400' : 'text-amber-400';
  const modeBanner = isCheckIn ? 'bg-emerald-950/20' : mode === 'review' ? 'bg-indigo-950/20' : 'bg-amber-950/20';
  const durationLabel = isCheckIn ? '60-90 sec' : '5-10 min';

  const handleEndCheckIn = async () => {
    if (closing) return;
    setClosing(true);
    try { voice.stopSpeaking(); } catch { /* noop */ }
    try { await endCheckIn(sessionId); } catch { /* close-anyway */ }
    onComplete && onComplete();
  };

  return (
    <div className="flex flex-col h-full max-w-3xl mx-auto">
      {/* Mode banner */}
      <div className={`flex items-center gap-2 px-4 py-2.5 border-b border-slate-800 ${modeBanner}`}>
        <ModeIcon size={16} className={modeColor} />
        <span className={`text-sm font-medium ${modeColor}`}>{modeLabel}</span>
        <span className="text-xs text-slate-500 ml-auto">{durationLabel}</span>
        {voice.available && (
          <button
            onClick={voice.toggleVoiceMode}
            title={voice.voiceMode ? 'Voice mode on — tap to disable' : 'Voice mode off — tap to enable'}
            aria-label={voice.voiceMode ? 'Disable voice mode' : 'Enable voice mode'}
            aria-pressed={voice.voiceMode}
            className={`flex items-center justify-center min-h-tap min-w-tap sm:min-h-0 sm:min-w-0 sm:w-8 sm:h-8 rounded-md transition-colors ${
              voice.voiceMode
                ? 'text-purple-300 bg-purple-900/30 hover:bg-purple-900/50'
                : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'
            }`}
          >
            {voice.voiceMode ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
        )}
      </div>

      {voice.error && (
        <div className="px-4 py-2 bg-red-900/20 border-b border-red-900/40 text-xs text-red-300 flex items-center justify-between">
          <span>{voice.error}</span>
          <button onClick={voice.clearError} className="text-red-400 hover:text-red-200" aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 sm:px-4 pb-4 pt-4 space-y-4">
        {messages.map((msg, i) => (
          <div key={i} className={`flex animate-fade-in-up ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[88%] sm:max-w-[80%] rounded-2xl px-3.5 sm:px-4 py-2.5 sm:py-3 text-sm leading-relaxed shadow-sm ${
              msg.role === 'user'
                ? 'bg-blue-600/20 text-blue-100 border border-blue-800/30 rounded-br-md'
                : 'bg-slate-800/50 text-slate-200 border border-slate-700/50 rounded-bl-md'
            }`}>
              {msg.thinking && !msg.content ? (
                <div className="flex items-center gap-2 text-slate-400">
                  <Loader2 size={14} className="animate-spin" />
                  <span className="text-xs">
                    {msg.toolName ? `${msg.toolName}...` : 'Thinking...'}
                  </span>
                </div>
              ) : (
                <div className="whitespace-pre-wrap">{msg.content}</div>
              )}
            </div>
          </div>
        ))}

        {loading && !streaming && (
          <div className="flex justify-start">
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3">
              <Loader2 size={16} className="animate-spin text-slate-400" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Continue button or Input */}
      <div className="border-t border-slate-800 px-3 sm:px-4 pt-3 pb-safe-2">
        {showContinue ? (
          <div className="max-w-3xl mx-auto">
            <button
              onClick={() => onComplete && onComplete()}
              className={`w-full ${
                mode === 'review'
                  ? 'bg-indigo-600 hover:bg-indigo-500'
                  : 'bg-blue-600 hover:bg-blue-500'
              } text-white rounded-lg py-3.5 min-h-tap text-sm font-medium transition-colors flex items-center justify-center gap-2 shadow-md`}
            >
              {mode === 'review' ? 'View Updated Plan' : "Continue to Today's Plan"}
              <ArrowRight size={16} />
            </button>
          </div>
        ) : isCheckIn ? (
          <div className="max-w-3xl mx-auto space-y-2">
            {voice.voiceMode
              ? <VoiceInput voice={voice} loading={loading} />
              : (
                <div className="flex gap-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => { setInput(e.target.value); autoGrow(e.target); }}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your response..."
                    rows={1}
                    className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder-slate-500 resize-none overflow-hidden focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                  <button
                    onClick={handleSend}
                    disabled={!input.trim() || loading}
                    aria-label="Send message"
                    className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed text-white rounded-lg px-4 min-w-tap transition-colors flex items-center justify-center"
                  >
                    <Send size={16} />
                  </button>
                </div>
              )}
            <button
              onClick={handleEndCheckIn}
              disabled={closing || loading || streaming}
              className="w-full text-xs text-slate-400 hover:text-emerald-300 bg-slate-800/40 hover:bg-emerald-900/20 border border-slate-700/50 hover:border-emerald-800/40 rounded-md py-2 min-h-tap transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
            >
              {closing ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              {closing ? 'Closing…' : 'Done'}
            </button>
          </div>
        ) : voice.voiceMode ? (
          <VoiceInput voice={voice} loading={loading} />
        ) : (
          <div className="flex gap-2 max-w-3xl mx-auto">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => { setInput(e.target.value); autoGrow(e.target); }}
              onKeyDown={handleKeyDown}
              placeholder="Type your response..."
              rows={1}
              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder-slate-500 resize-none overflow-hidden focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              aria-label="Send message"
              className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed text-white rounded-lg px-4 min-w-tap transition-colors flex items-center justify-center"
            >
              <Send size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
